import { useCallback, useRef, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { isAfter, parseISO, startOfDay } from "date-fns";
import { useIsMutating } from "@tanstack/react-query";
import {
    RadioInput,
    ActionButton,
    Button,
    DatePicker,
    Dialog,
    DialogContent,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    FormCheckbox,
    Input,
    Select,
    TextArea,
} from "@jhool-io/fe-components";
import {
    displayNameInRightFormat,
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../../utils/helpers";
import LoadPreviousSessionNotificationModal from "../../LoadPreviousSessionNotificatiModal/LoadPreviousSessionNotificationModal";
import NoteFormInfoBox from "../../NoteFormInfoBox/NoteFormInfoBox";
import {
    ConditionStatus,
    ConditionType,
    IEditTermOfCareNoteMedPayload,
    IGetClientNotes,
    ISessionNote,
    Modality,
    SessionLocations,
} from "../../../../modules/notes/types/notes.types";
import {
    useEditNote,
    useNoteAutosave,
    useNoteValidationFeedback,
} from "../../../../modules/notes/hooks/edit-note";
import EditNoteFooter from "../../../../modules/notes/components/NoteCreationFooters/EditNoteFooter/EditNoteFooter";
import { TERM_OF_CARE_NOTE_MED_LABELS } from "../../../../utils/constants";
import { useFetchClientNotesList } from "../../../../hooks/queries/note";
import ViewFile from "../../../../shared-ui/ViewFile/ViewFile";
import DiagnosisCodeSelect from "../../../../modules/notes/components/DiagnosisCodeSelect/DiagnosisCodeSelect";

import * as FilePicker from "../../../../shared-ui/FilePicker/FilePicker";
import * as Progress from "../../../../shared-ui/Progress/Progress";
import DeleteIcon from "../../../../icons/Delete";
import { IViewFile } from "../../../../utils/types";
import MedNoteEPrescribe from "../../../../modules/e-prescirbe/components/MedNoteEPrescribe/MedNoteEPrescribe";

interface EditTermOfCareNoteMedProps {
    noteDetails: ISessionNote;
}

type Option = {
    label: React.ReactNode;
    value: string;
};

const PATIENT_FINAL_CONDITION_OPTIONS = [
    "Much improved",
    "Improved",
    "Worse",
    "No change",
    "Stable",
    "Unstable",
];

const PROGNOSIS_OPTIONS = ["Good", "Poor", "Guarded"];

export default function EditTermOfCareNoteMed({
    noteDetails,
}: EditTermOfCareNoteMedProps) {
    const [showPrompt, setShowPrompt] = useState(false);
    const [selectedNote, setSelectedNote] = useState({} as IGetClientNotes);
    const [docInView, setDocInView] = useState<IViewFile | null>(null);
    const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
    const [localDateOfFirstSession, setLocalDateOfFirstSession] =
        useState<Date | null>(
            noteDetails.note_content?.date_of_first_session
                ? parseISO(noteDetails.note_content.date_of_first_session)
                : null
        );
    const [localDateOfLastSession, setLocalDateOfLastSession] =
        useState<Date | null>(
            noteDetails.note_content?.date_of_last_session
                ? parseISO(noteDetails.note_content.date_of_last_session)
                : null
        );

    const clientNoteParams = {
        type: noteDetails?.type as string,
        load_previous_notes: true,
        provider_id: noteDetails?.provider?.provider_id as string,
    };

    const isSigning = useRef(false);

    const { data: notes } = useFetchClientNotesList(
        noteDetails?.client?.client_id as string,
        clientNoteParams
    );

    const {
        handleAutoSaveNote,
        handleEditDraftNote,
        handleSignNote,
        handleAddNoteDocument,
        handleDeleteNoteContentDocument,
        uploadProgress,
    } = useEditNote();

    const {
        register,
        handleSubmit,
        getValues,
        setValue,
        watch,
        control,
        trigger,
        formState: { errors, submitCount },
    } = useForm<IEditTermOfCareNoteMedPayload>({
        resolver: yupResolver(
            yup.object({
                diagnosis_codes: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .when([], {
                        is: () => isSigning.current,
                        then: yup
                            .array()
                            .min(1, "Primary diagnosis is required"),
                        otherwise: yup.array(),
                    }),
                secondary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                tertiary_diagnosis: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_one: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                additional_diagnosis_two: yup
                    .array()
                    .max(1, "You can only select 1 diagnosis")
                    .nullable(),
                date_of_first_session: yup.date().when([], {
                    is: () => isSigning.current,
                    then: yup.date().required("This field is required"),
                    otherwise: yup.date().nullable(),
                }),
                date_of_last_session: yup
                    .date()
                    .when([], {
                        is: () => isSigning.current,
                        then: yup.date().required("This field is required"),
                        otherwise: yup.date().nullable(),
                    })
                    .test(
                        "is-after-or-equal",
                        "Date of last session cannot be earlier than date of first session",
                        (value, context) => {
                            const { date_of_first_session: firstSession } =
                                context.parent;

                            if (!firstSession || !value) return true;

                            return !isAfter(
                                startOfDay(firstSession),
                                startOfDay(value)
                            );
                        }
                    ),
                total_number_of_therapy_sessions: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                presenting_concerns: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                planned_treatment_and_goals: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                course_of_treatment: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                patient_final_condition: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "Please select at least one option")
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                prognosis: yup.array().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .array()
                        .min(1, "Please select at least one option")
                        .required("Please select at least one option"),
                    otherwise: yup.array().nullable(),
                }),
                reason_for_termination: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                discharge_plan_and_follow_up: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_type: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_type_additional_details: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_status: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                condition_status_additional_details: yup.string(),
                modality: yup.string(),
                location_of_the_client: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Client location is required")
                            : field
                    ),
                other_location_of_the_client: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_client"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other client location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when("session_location", (sessionLocation, field) =>
                        sessionLocation === SessionLocations.Telehealth &&
                        isSigning.current
                            ? field.required("Provider location is required")
                            : field
                    ),
                other_location_of_the_provider: yup
                    .string()
                    .nullable()
                    .when(["session_location", "location_of_the_provider"], {
                        is: (sessionLocation?: string, location?: string) =>
                            sessionLocation === SessionLocations.Telehealth &&
                            location === "other" &&
                            isSigning.current,
                        then: yup
                            .string()
                            .nullable()
                            .trim()
                            .required("Other provider location is required"),
                        otherwise: yup.string().nullable(),
                    }),
                consent_was_received_for_the_teletherapy_session: yup.bool(),
                the_session_was_conducted_via_video: yup.bool(),
                session_location: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup
                        .string()
                        .nullable()
                        .trim()
                        .required("This field is required"),
                    otherwise: yup.string(),
                }),
                signature: yup.string().when([], {
                    is: () => isSigning.current,
                    then: yup.string().trim().required("Signature is required"),
                    otherwise: yup.string(),
                }),
            })
        ),
        mode: "onChange",
        defaultValues: noteDetails.note_content
            ? {
                  ...noteDetails.note_content,
                  secondary_diagnosis: noteDetails?.note_content
                      ?.secondary_diagnosis
                      ? [noteDetails?.note_content?.secondary_diagnosis]
                      : undefined,
                  tertiary_diagnosis: noteDetails?.note_content
                      ?.tertiary_diagnosis
                      ? [noteDetails?.note_content?.tertiary_diagnosis]
                      : undefined,
                  additional_diagnosis_one: noteDetails?.note_content
                      ?.additional_diagnosis_one
                      ? [noteDetails?.note_content?.additional_diagnosis_one]
                      : undefined,
                  additional_diagnosis_two: noteDetails?.note_content
                      ?.additional_diagnosis_two
                      ? [noteDetails?.note_content?.additional_diagnosis_two]
                      : undefined,
              }
            : undefined,
        shouldFocusError: false,
    });

    const watchedValues = watch();

    const isAddDocMutating = useIsMutating(["add-note-content-document"]);
    const isDeleteDocMutating = useIsMutating(["delete-note-content-document"]);

    const onDrop = useCallback(
        (acceptedFiles: File[]) => {
            if (selectedFiles && selectedFiles.length > 0) {
                setSelectedFiles([...acceptedFiles, ...selectedFiles]);
            } else {
                setSelectedFiles([...acceptedFiles]);
            }
        },
        [selectedFiles]
    );

    const handleRemoveSelectedFile = (filename: string) => {
        if (selectedFiles) {
            const filesArr = [...selectedFiles].filter(
                (file) => file.name !== filename
            );
            if (filesArr.length === 0) setSelectedFiles(null);
            else setSelectedFiles(filesArr);
        }
    };

    const handleLoadPreviousSession = (note: IGetClientNotes) => {
        setValue(
            "location_of_the_client",
            note.note_content.location_of_the_client
        );
        setValue(
            "other_location_of_the_client",
            note.note_content.other_location_of_the_client
        );
        setValue(
            "location_of_the_provider",
            note.note_content.location_of_the_provider
        );
        setValue(
            "other_location_of_the_provider",
            note.note_content.other_location_of_the_provider
        );
        setValue(
            "consent_was_received_for_the_teletherapy_session",
            note.note_content.consent_was_received_for_the_teletherapy_session
        );
        setValue(
            "the_session_was_conducted_via_video",
            note.note_content.the_session_was_conducted_via_video
        );

        const dateOfFirstSession =
            note.note_content.date_of_first_session ?? null;
        const dateOfLastSession =
            note.note_content.date_of_last_session ?? null;
        setValue("diagnosis_codes", note.note_content.diagnosis_codes);
        setValue(
            "secondary_diagnosis",
            note.note_content.secondary_diagnosis
                ? [note.note_content.secondary_diagnosis]
                : undefined
        );
        setValue(
            "tertiary_diagnosis",
            note.note_content.tertiary_diagnosis
                ? [note.note_content.tertiary_diagnosis]
                : undefined
        );
        setValue(
            "additional_diagnosis_one",
            note.note_content.additional_diagnosis_one
                ? [note.note_content.additional_diagnosis_one]
                : undefined
        );
        setValue(
            "additional_diagnosis_two",
            note.note_content.additional_diagnosis_two
                ? [note.note_content.additional_diagnosis_two]
                : undefined
        );
        setValue("date_of_first_session", dateOfFirstSession);
        setLocalDateOfFirstSession(
            dateOfFirstSession ? parseISO(dateOfFirstSession) : null
        );
        setValue("date_of_last_session", dateOfLastSession);
        setLocalDateOfLastSession(
            dateOfLastSession ? parseISO(dateOfLastSession) : null
        );
        setValue(
            "total_number_of_therapy_sessions",
            note.note_content.total_number_of_therapy_sessions
        );
        setValue("presenting_concerns", note.note_content.presenting_concerns);
        setValue(
            "planned_treatment_and_goals",
            note.note_content.planned_treatment_and_goals
        );
        setValue("course_of_treatment", note.note_content.course_of_treatment);
        setValue(
            "patient_final_condition",
            note.note_content.patient_final_condition
        );
        setValue("prognosis", note.note_content.prognosis);
        setValue(
            "reason_for_termination",
            note.note_content.reason_for_termination
        );
        setValue(
            "discharge_plan_and_follow_up",
            note.note_content.discharge_plan_and_follow_up
        );
        setValue("condition_type", note.note_content.condition_type);
        setValue(
            "condition_type_additional_details",
            note.note_content.condition_type_additional_details
        );
        setValue("condition_status", note.note_content.condition_status);
        setValue(
            "condition_status_additional_details",
            note.note_content.condition_status_additional_details
        );
        setValue("modality", note.note_content.modality);
        setValue("session_location", note.note_content.session_location);
        setShowPrompt(false);
    };

    const handleIsChecked = (checkboxValue: string, values?: string[]) => {
        return Boolean(
            values?.find(
                (value: string) => value.trim() === checkboxValue.trim()
            )
        );
    };

    const conditionTypeOptionsForSelect = Object.keys(ConditionType).map(
        (conditionType) => ({
            label: removeEnumUnderscore(conditionType),
            value: ConditionType[conditionType as keyof typeof ConditionType],
        })
    );

    const conditionStatusOptionsForSelect = Object.keys(ConditionStatus).map(
        (conditionStatus) => ({
            label: removeEnumUnderscore(conditionStatus),
            value: ConditionStatus[
                conditionStatus as keyof typeof ConditionStatus
            ],
        })
    );

    const sessionLocationOptionsForSelect = Object.keys(SessionLocations).map(
        (location) => ({
            label: removeEnumUnderscore(location),
            value: SessionLocations[location as keyof typeof SessionLocations],
        })
    );

    const modalityOptionsForSelect = Object.keys(Modality).map((modality) => ({
        label: removeEnumUnderscore(modality),
        value: Modality[modality as keyof typeof Modality],
    }));

    useNoteValidationFeedback({
        errors,
        submitCount,
        isSigningRef: isSigning,
        trigger,
    });

    useNoteAutosave({
        watchedValues,
        getValues,
        handleSubmit,
        onSave: handleAutoSaveNote,
        isSigningRef: isSigning,
    });

    return (
        <>
            {docInView ? (
                <Dialog open={Boolean(docInView)}>
                    <DialogContent
                        title={
                            docInView?.document_name ??
                            "Note content attachment"
                        }
                        handleCloseDialog={() => {
                            setDocInView(null);
                        }}
                        showFooter={false}
                    >
                        <ViewFile
                            url={docInView.url}
                            contentType={
                                docInView?.metadata?.content_type ?? ""
                            }
                            title={
                                docInView?.document_name ??
                                "Note content attachment"
                            }
                        />
                    </DialogContent>
                </Dialog>
            ) : null}
            <LoadPreviousSessionNotificationModal
                showPrompt={showPrompt}
                setShowPrompt={setShowPrompt}
                handleLoadPreviousSession={() =>
                    handleLoadPreviousSession(selectedNote)
                }
            />

            {notes && notes.data.length > 0 && (
                <div className="flex justify-end mb-[--spacing-header]">
                    <Dropdown>
                        <DropdownTrigger asChild>
                            <FilterButton>
                                Load previous session note
                            </FilterButton>
                        </DropdownTrigger>
                        <DropdownContent width="auto" align="end">
                            {notes &&
                                notes?.data?.map((note) => (
                                    <DropdownItem
                                        key={note.note_id}
                                        onClick={() => {
                                            setShowPrompt(true);
                                            setSelectedNote(note);
                                        }}
                                    >
                                        <div>
                                            {makeStringFirstLetterCapital(
                                                removeEnumUnderscore(
                                                    note.note_type
                                                )
                                            )}{" "}
                                            - Note content Details -{" "}
                                            {formatDate(note.date_of_service)}
                                        </div>
                                    </DropdownItem>
                                ))}
                        </DropdownContent>
                    </Dropdown>
                </div>
            )}

            <form
                onSubmit={handleSubmit(handleSignNote)}
                aria-label="term of care note form"
                id="edit-note-form"
            >
                <div className="mb-[--spacing-header]">
                    <NoteFormInfoBox />
                </div>
                <div className="fg">
                    <Controller
                        name="session_location"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.session_location
                                }
                                placeholder=""
                                options={sessionLocationOptionsForSelect}
                                onChange={(val) =>
                                    field.onChange(
                                        (val as { value: string }).value
                                    )
                                }
                                value={
                                    sessionLocationOptionsForSelect.find(
                                        (option) => option.value === field.value
                                    ) || null
                                }
                                hasError={!!errors.session_location}
                                errorText={errors.session_location?.message}
                            />
                        )}
                    />
                </div>
                {watch("session_location") === SessionLocations.Telehealth && (
                    <>
                        <h3 className="note-label-req">
                            {
                                TERM_OF_CARE_NOTE_MED_LABELS.location_of_the_client
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_client")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_client === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_client ? (
                            <div className="note-error">
                                <p>{errors.location_of_the_client?.message}</p>
                            </div>
                        ) : null}
                        {watch("location_of_the_client") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_client"
                                    )}
                                    label={
                                        TERM_OF_CARE_NOTE_MED_LABELS.other_location_of_the_client
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_client
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_client
                                    }
                                    errorText={
                                        errors.other_location_of_the_client
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <h3 className="note-label-req">
                            {
                                TERM_OF_CARE_NOTE_MED_LABELS.location_of_the_provider
                            }
                        </h3>
                        <div className="fg flex gap-x-5">
                            {["home", "office", "other"].map((value) => (
                                <RadioInput
                                    key={value}
                                    {...register("location_of_the_provider")}
                                    label={makeStringFirstLetterCapital(value)}
                                    value={value}
                                    defaultChecked={
                                        noteDetails.note_content
                                            ?.location_of_the_provider === value
                                    }
                                />
                            ))}
                        </div>
                        {errors.location_of_the_provider ? (
                            <div className="note-error">
                                <p>
                                    {errors.location_of_the_provider?.message}
                                </p>
                            </div>
                        ) : null}
                        {watch("location_of_the_provider") === "other" && (
                            <div className="fg">
                                <Input
                                    {...register(
                                        "other_location_of_the_provider"
                                    )}
                                    label={
                                        TERM_OF_CARE_NOTE_MED_LABELS.other_location_of_the_provider
                                    }
                                    defaultValue={
                                        noteDetails.note_content
                                            ?.other_location_of_the_provider
                                    }
                                    isRequired
                                    hasError={
                                        !!errors.other_location_of_the_provider
                                    }
                                    errorText={
                                        errors.other_location_of_the_provider
                                            ?.message
                                    }
                                />
                            </div>
                        )}
                        <div className="note-checkboxes">
                            <FormCheckbox
                                {...register(
                                    "consent_was_received_for_the_teletherapy_session"
                                )}
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.consent_was_received_for_the_teletherapy_session
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.consent_was_received_for_the_teletherapy_session
                                }
                            />
                            <FormCheckbox
                                {...register(
                                    "the_session_was_conducted_via_video"
                                )}
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.the_session_was_conducted_via_video
                                }
                                defaultChecked={
                                    noteDetails.note_content
                                        ?.the_session_was_conducted_via_video
                                }
                            />
                        </div>
                    </>
                )}

                <div className="fg">
                    <Controller
                        name="diagnosis_codes"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.diagnosis_codes
                                }
                                value={field.value}
                                onChange={field.onChange}
                                isRequired
                                errorText={
                                    errors.diagnosis_codes?.type === "typeError"
                                        ? "Diagnosis codes are required"
                                        : errors.diagnosis_codes?.message
                                }
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="secondary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.secondary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.secondary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="tertiary_diagnosis"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.tertiary_diagnosis
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={errors.tertiary_diagnosis?.message}
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_one"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.additional_diagnosis_one
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_one?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="additional_diagnosis_two"
                        control={control}
                        render={({ field }) => (
                            <DiagnosisCodeSelect
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.additional_diagnosis_two
                                }
                                value={field.value}
                                onChange={field.onChange}
                                errorText={
                                    errors.additional_diagnosis_two?.message
                                }
                                isOptional
                                isMulti
                            />
                        )}
                    />
                </div>
                <MedNoteEPrescribe
                    noteDetails={noteDetails}
                    showDiagnosisSync
                />
                <h3 className="note-header">
                    Termination of Treatment Summary
                </h3>
                <div className="fg fg-space-between two flex">
                    <Controller
                        name="date_of_first_session"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.date_of_first_session
                                }
                                onChange={async (date) => {
                                    field.onChange(date ?? null);
                                    setLocalDateOfFirstSession(date);
                                    await trigger("date_of_last_session");
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localDateOfFirstSession}
                                hasError={Boolean(errors.date_of_first_session)}
                                errorText={
                                    errors.date_of_first_session?.message
                                }
                            />
                        )}
                    />
                    <Controller
                        name="date_of_last_session"
                        control={control}
                        render={({ field }) => (
                            <DatePicker
                                isRequired
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.date_of_last_session
                                }
                                onChange={(date) => {
                                    field.onChange(date ?? null);
                                    setLocalDateOfLastSession(date);
                                }}
                                value={
                                    field.value
                                        ? formatDate(field.value, true)
                                        : undefined
                                }
                                selected={localDateOfLastSession}
                                hasError={Boolean(errors.date_of_last_session)}
                                errorText={errors.date_of_last_session?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <Input
                        {...register("total_number_of_therapy_sessions")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.total_number_of_therapy_sessions
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.total_number_of_therapy_sessions
                        }
                        isRequired
                        hasError={!!errors.total_number_of_therapy_sessions}
                        errorText={
                            errors.total_number_of_therapy_sessions?.message
                        }
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("presenting_concerns")}
                        label={TERM_OF_CARE_NOTE_MED_LABELS.presenting_concerns}
                        defaultValue={
                            noteDetails.note_content?.presenting_concerns
                        }
                        isRequired
                        hasError={!!errors.presenting_concerns}
                        errorText={errors.presenting_concerns?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("planned_treatment_and_goals")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.planned_treatment_and_goals
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.planned_treatment_and_goals
                        }
                        isRequired
                        hasError={!!errors.planned_treatment_and_goals}
                        errorText={errors.planned_treatment_and_goals?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("course_of_treatment")}
                        label={TERM_OF_CARE_NOTE_MED_LABELS.course_of_treatment}
                        defaultValue={
                            noteDetails.note_content?.course_of_treatment
                        }
                        isRequired
                        hasError={!!errors.course_of_treatment}
                        errorText={errors.course_of_treatment?.message}
                    />
                </div>
                <h3 className="note-label note-label-req">
                    {TERM_OF_CARE_NOTE_MED_LABELS.patient_final_condition}
                </h3>
                <div className="note-checkboxes">
                    {PATIENT_FINAL_CONDITION_OPTIONS.map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register("patient_final_condition")}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content
                                    ?.patient_final_condition
                            )}
                        />
                    ))}
                </div>
                {errors.patient_final_condition ? (
                    <div className="note-error">
                        <p>{errors.patient_final_condition?.message}</p>
                    </div>
                ) : null}
                <h3 className="note-label note-label-req">
                    {TERM_OF_CARE_NOTE_MED_LABELS.prognosis}
                </h3>
                <div className="note-checkboxes">
                    {PROGNOSIS_OPTIONS.map((value) => (
                        <FormCheckbox
                            key={value}
                            {...register("prognosis")}
                            label={value}
                            value={value}
                            defaultChecked={handleIsChecked(
                                value,
                                noteDetails.note_content?.prognosis
                            )}
                        />
                    ))}
                </div>
                {errors.prognosis ? (
                    <div className="note-error">
                        <p>{errors.prognosis?.message}</p>
                    </div>
                ) : null}
                <div className="fg">
                    <TextArea
                        {...register("reason_for_termination")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.reason_for_termination
                        }
                        defaultValue={
                            noteDetails.note_content?.reason_for_termination
                        }
                        isRequired
                        hasError={!!errors.reason_for_termination}
                        errorText={errors.reason_for_termination?.message}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("discharge_plan_and_follow_up")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.discharge_plan_and_follow_up
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.discharge_plan_and_follow_up
                        }
                        isRequired
                        hasError={!!errors.discharge_plan_and_follow_up}
                        errorText={errors.discharge_plan_and_follow_up?.message}
                    />
                </div>
                <h3 className="note-header">Condition Type & Status</h3>
                <div className="fg">
                    <Controller
                        name="condition_type"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.condition_type
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionTypeOptionsForSelect.find(
                                        (type) => type.value === field.value
                                    ) || null
                                }
                                options={conditionTypeOptionsForSelect}
                                hasError={!!errors.condition_type}
                                errorText={errors.condition_type?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_type_additional_details")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.condition_type_additional_details
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.condition_type_additional_details
                        }
                        isRequired
                        hasError={!!errors.condition_type_additional_details}
                        errorText={
                            errors.condition_type_additional_details?.message
                        }
                    />
                </div>
                <div className="fg">
                    <Controller
                        name="condition_status"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isRequired
                                label={
                                    TERM_OF_CARE_NOTE_MED_LABELS.condition_status
                                }
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    conditionStatusOptionsForSelect.find(
                                        (status) => status.value === field.value
                                    ) || null
                                }
                                options={conditionStatusOptionsForSelect}
                                hasError={!!errors.condition_status}
                                errorText={errors.condition_status?.message}
                            />
                        )}
                    />
                </div>
                <div className="fg">
                    <TextArea
                        {...register("condition_status_additional_details")}
                        label={
                            TERM_OF_CARE_NOTE_MED_LABELS.condition_status_additional_details
                        }
                        defaultValue={
                            noteDetails.note_content
                                ?.condition_status_additional_details
                        }
                        isOptional
                    />
                </div>
                <div className="fg fg-space-between two flex">
                    <Controller
                        name="modality"
                        control={control}
                        render={({ field }) => (
                            <Select
                                isOptional
                                label={TERM_OF_CARE_NOTE_MED_LABELS.modality}
                                placeholder=""
                                onChange={(val) =>
                                    field.onChange((val as Option).value)
                                }
                                value={
                                    modalityOptionsForSelect.find(
                                        (modality) =>
                                            modality.value === field.value
                                    ) || null
                                }
                                options={modalityOptionsForSelect}
                            />
                        )}
                    />
                </div>

                <h3 className="note-label-opt">
                    {TERM_OF_CARE_NOTE_MED_LABELS.files}
                </h3>
                <div className="fg flex flex-col gap-y-[--spacing-form-items]">
                    <FilePicker.FilePicker
                        onDrop={onDrop}
                        disabled={isAddDocMutating > 0}
                    >
                        <FilePicker.FilePickerIcon />
                        <FilePicker.FilePickerMeta description="Click to upload note files" />
                    </FilePicker.FilePicker>
                    {selectedFiles && selectedFiles.length > 0 ? (
                        <div className="flex flex-col gap-y-[--spacing-items]">
                            <FilePicker.FilePickerSelectedFiles
                                selectedFiles={selectedFiles}
                                handleRemoveSelectedFile={
                                    handleRemoveSelectedFile
                                }
                                wrapperClassName="grid grid-cols-[repeat(auto-fill,minmax(250px,1fr))] grid-flow-row flex-row gap-[--spacing-items]"
                            />
                            <Button
                                className="self-start"
                                onClick={() =>
                                    handleAddNoteDocument(selectedFiles, () =>
                                        setSelectedFiles(null)
                                    )
                                }
                                disabled={isAddDocMutating > 0}
                            >
                                Save files
                            </Button>
                            {uploadProgress > 0 && uploadProgress < 100 && (
                                <Progress.Root value={uploadProgress}>
                                    <Progress.Label>
                                        Uploading {selectedFiles.length}{" "}
                                        {selectedFiles.length === 1
                                            ? "file"
                                            : "files"}
                                        ...
                                    </Progress.Label>
                                    <Progress.Value />
                                    <Progress.Track>
                                        <Progress.Indicator />
                                    </Progress.Track>
                                </Progress.Root>
                            )}
                        </div>
                    ) : null}
                    {noteDetails?.note_content_documents &&
                    noteDetails?.note_content_documents.length > 0 ? (
                        <div className="flex flex-col gap-y-[var(--spacing-items)]">
                            {noteDetails.note_content_documents.map((doc) => (
                                <div
                                    key={doc.note_content_document_id}
                                    className="flex items-center gap-x-3"
                                >
                                    <ActionButton
                                        variant="can-hover"
                                        size="auto"
                                        className="h-7 !text-text text-caption underline"
                                        actionTitle="View file"
                                        onClick={() => {
                                            setDocInView(doc);
                                        }}
                                    >
                                        <span>{`${displayNameInRightFormat(
                                            {
                                                firstName:
                                                    doc.uploaded_by.first_name,
                                                lastName:
                                                    doc.uploaded_by.last_name,
                                            },
                                            false
                                        )} -  ${doc.document_name}`}</span>
                                    </ActionButton>
                                    <ActionButton
                                        variant="can-hover"
                                        aria-label="delete icon"
                                        size="auto"
                                        className="h-7"
                                        onClick={() =>
                                            handleDeleteNoteContentDocument(
                                                doc.note_content_document_id
                                            )
                                        }
                                        disabled={isDeleteDocMutating > 0}
                                        actionTitle="Delete file from note content"
                                    >
                                        <DeleteIcon />
                                    </ActionButton>
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
                <div className="fg-info fg-line">
                    <p>Sign note here</p>
                    <div className="fg">
                        <Input
                            {...register("signature")}
                            hasError={Boolean(errors.signature)}
                            errorText={errors.signature?.message}
                            label="Provider's Initials"
                            autoComplete="off"
                            isRequired
                        />
                    </div>
                </div>
                <div>
                    <EditNoteFooter
                        onSaveButtonClick={() => {
                            isSigning.current = false;
                            handleSubmit(handleEditDraftNote)();
                        }}
                        noteDOS={noteDetails.date_of_service}
                        onSubmitButtonClick={() => {
                            isSigning.current = true;
                        }}
                    />
                </div>
            </form>
        </>
    );
}
